import type { CnpjExistente as Dados } from '../useRascunho'

/**
 * Desfecho do `iniciar` quando o CNPJ já está vivo na esteira.
 *
 * Substitui o gate de código como única barreira do canal público. O ponto é
 * pegar a duplicidade ANTES de a pessoa preencher sete telas e subir nove
 * documentos — antes, isso só aparecia no envio.
 *
 * Os dois casos pedem encaminhamentos diferentes, e é por isso que a tela
 * distingue em vez de mostrar um texto genérico:
 *
 *   emAnalise   — já existe pedido correndo. Insistir só gera trabalho
 *                 duplicado para Crédito e Cobrança, e a resposta chega igual.
 *   cadastrado  — já é cliente. O que a pessoa quer aqui é quase sempre comprar
 *                 ou atualizar dado, não se cadastrar de novo.
 *
 * Deliberadamente NÃO mostra o número da solicitação: ele é sequencial, e quem
 * sondasse dois CNPJs inferiria o volume mensal da empresa.
 */
export function CnpjExistente({
  dados,
  cnpj,
  aoRecomecar,
}: {
  dados: Dados
  cnpj: string
  aoRecomecar: () => void
}) {
  const emAnalise = dados.situacao === 'emAnalise'

  return (
    <div className="grade">
      <p className={emAnalise ? 'aviso' : 'aviso aviso--ok'} role="status">
        {emAnalise ? (
          <>
            Já recebemos uma solicitação de pré-cadastro para o CNPJ{' '}
            <strong>{formatarCnpj(cnpj)}</strong>
            {dados.desde ? <> em {formatarData(dados.desde)}</> : null}, e ela está em análise.
          </>
        ) : (
          <>
            O CNPJ <strong>{formatarCnpj(cnpj)}</strong> já possui cadastro ativo na Nazária
            Distribuidora.
          </>
        )}
      </p>

      <h3 className="secao">O que fazer agora</h3>
      {emAnalise ? (
        <ol className="passos">
          <li>
            Não é preciso enviar de novo. Um segundo pedido não acelera a análise e é tratado
            como duplicado.
          </li>
          <li>
            Para saber do andamento ou corrigir alguma informação, fale com seu representante
            comercial.
          </li>
        </ol>
      ) : (
        <ol className="passos">
          <li>Para comprar, fale com seu representante comercial.</li>
          <li>Para atualizar dados cadastrais, fale com seu representante comercial.</li>
        </ol>
      )}

      <p className="nota">
        Digitou o CNPJ errado? <strong>Corrija e tente de novo</strong> — nada foi gravado.
      </p>

      <div className="rodape">
        <span className="rodape__espaco" />
        <button type="button" className="botao botao--secundario" onClick={aoRecomecar}>
          Corrigir o CNPJ
        </button>
      </div>
    </div>
  )
}

function formatarCnpj(valor: string) {
  const limpo = valor.replace(/[^0-9A-Za-z]/g, '').toUpperCase()
  if (limpo.length !== 14) return valor
  return `${limpo.slice(0, 2)}.${limpo.slice(2, 5)}.${limpo.slice(5, 8)}/${limpo.slice(8, 12)}-${limpo.slice(12)}`
}

function formatarData(iso: string) {
  /* `new Date('2026-08-15')` é lido como UTC e vira 14/08 em qualquer fuso
     negativo — o Brasil inteiro. Por isso a data é partida à mão. */
  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!partes) return iso
  return `${partes[3]}/${partes[2]}/${partes[1]}`
}
